// ============================================================
// DeadlinesSection.tsx
// Per-case deadline rows.
//
// Stage 3 redesign (approved mockup casevo-case-sections-batch2.html §3): a
// LIST section in the shared card language (like Witnesses) —
//   • the LIMITATION date pinned in its own card (legally critical): green
//     tile, day countdown, basis, where it shows; when MISSING a dashed amber
//     warning with the accident + 3y estimate and a one-click "Add limitation
//     date" that creates the real deadline (so the hero drops "est." and the
//     daily scan alerts on it)
//   • rows grouped Overdue / Due soon (≤ 3 days, incl. today) / Upcoming /
//     Completed — the Dashboard + deadline-scan rule (DEADLINE_SOON_DAYS = 3)
//   • each row: complete tick, date tile, type, status chip, relative date,
//     the notes (LIVE read-only doc), card presence, pencil + ⋯ menu; edit =
//     due date + live notes (type read-only after create) with Cancel / Save
//   • "Add deadline" in the header opens a New deadline card (type picker,
//     due date, optional notes); the calendar's "new deadline on this day"
//     prefill opens it with the date set
//   • an alerts footer (Deadlines isn't in the setup chain — no Mark complete)
//
// DATA LOGIC IS UNCHANGED from the old form (b9a554e):
//   - ADD posts { deadlineTypeId, dueDate, notes } (createCaseDeadlineApi) and
//     captures the row's concurrency base (updatedAt + values)
//   - date edits: optimistic store update + debounced PATCH
//     (scheduleDeadlineSave); notes = Yjs doc `deadline:<id>:notes` with the
//     HTML mirror via the same per-row autosave (handleNotesSnapshot)
//   - completed toggles an IMMEDIATE PATCH merging any drained pending
//     payload, with base + 409 handling and rollback (handleToggleCompleted)
//   - delete via ConfirmDialogue, cancelling the row's pending PATCH first
// ============================================================
import { refusalText } from '../../utils/serverError'
import { caseDobOf } from '../../utils/caseDob'
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import FormField from '../shared/FormField'
import DateInput from '../shared/DateInput'
import FieldError from '../shared/FieldError'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import RichTextEditor from '../shared/RichTextEditor'
import CardPresence from '../shared/CardPresence'
import { useCollabUser } from '../../hooks/useCollabUser'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { deadlineScope, getFieldRefusal, useFieldRefusal } from '../../hooks/fieldRefusals'
import DOMPurify from 'dompurify'
import {
  listDeadlineTypesApi,
  createCaseDeadlineApi,
  updateCaseDeadlineApi,
  deleteCaseDeadlineApi,
} from '../../api/casevoApi'
import {
  scheduleDeadlineSave,
  cancelDeadlineSave,
  consumePendingDeadlinePayload,
  flushDeadlineAutoSave,
} from '../../hooks/useCaseDeadlineAutoSave'
import { getDeadlineBaseUpdatedAt, setDeadlineBaseUpdatedAt, setDeadlineBaseValues } from '../../hooks/saveBaseVersion'
import { flushLiveDocSnapshots, saveDidFail, SAVE_FAILED_MSG } from '../shared/liveDocFlush'
import { addYearsCivil } from '../../utils/caseHelpers'
import { useFieldFocusRequest } from '../../utils/fieldFocusRequest'
import { LIMITATION_FIELD_KEY } from '../shared/requiredFields'
import { limitationChecksOf, limitationLaterCheckOf, limitationOf, limitationPassedOf, limitationRowOf, noTimeLimitOf } from '../case/caseFacts'
import { childWording, diseaseWording, estimateBadge, estimateBasisText, estimateStartOf, isLimitationName, limitationDateCheck, LIMITATION_PASSED, noEstimateHint, type LimitationBasis, type LimitationCheck, type LimitationClaimFacts, type DobInput } from '../../utils/limitationRules'
import { claimFactsOf } from '../../utils/caseClaim'
import { ymdOf, todayYmd, daysBetween, relDays, fmtDay } from '../case/caseUi'
import { CARD, CARD_EDITING, BTN_SEC, BTN_PRI, SM } from '../clientInfo/ClientInfoSections'
import { IcEdit, IcCheck, inpCls, SELECT_BG, FGRID } from '../clientInfo/clientInfoRegistry'
import { MENU_SURFACE, MenuItem, MenuIcon, MenuSep } from '../shared/menuUi'
import { KIcon, SectionHead, Glance, GlanceDash, LiveDocEdit, LiveDocView, LiveNote, htmlText } from '../case/sections/sectionKit'
import { textDateToIso } from '../../utils/civilDate'
import { confirmedLine } from './AbuseNoLimitBlock'
import { useFirmChange } from '../../utils/liveEvents'
import type {
  CaseDeadline,
  DeadlineType,
  UpdateCaseDeadlinePayload,
} from '../../types/deadlines.types'

// Due-soon window — the Dashboard (DashboardView "soon" ≤ 3 days) and the
// daily scan (backend deadlineTaskScan DEADLINE_SOON_DAYS = 3) use the same.
const SOON_DAYS = 3
const LIMITATION_RE = /limitation/i

type Kind = 'overdue' | 'today' | 'soon' | 'later' | 'done'
const kindOf = (d: CaseDeadline, today: string): Kind => {
  if (d.completed) return 'done'
  const ymd = ymdOf(d.dueDate)
  if (!ymd) return 'later'
  const n = daysBetween(today, ymd)
  return n < 0 ? 'overdue' : n === 0 ? 'today' : n <= SOON_DAYS ? 'soon' : 'later'
}

// ── status recipe (mockup ST; red / amber / green / done) ──
const RED = 'bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]'
const WARN = 'bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]'
const CHIP_CLS: Record<Kind, string> = {
  overdue: RED,
  today: WARN,
  soon: WARN,
  later: 'bg-casevo-mint/80 text-casevo-brand dark:text-[#8fd6b0]',
  done: 'bg-casevo-border-soft text-casevo-text-soft',
}
const CHIP_LABEL: Record<Kind, string> = { overdue: 'Overdue', today: 'Due today', soon: 'Due soon', later: 'Upcoming', done: 'Done' }
// 4px inset left accent on overdue / soon rows (+ the card shadow; `!` beats
// CARD's). Literal classes (Tailwind can't see runtime-built ones).
const ACCENT = {
  red: '!shadow-[inset_4px_0_0_#c23b2c,0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:!shadow-[inset_4px_0_0_#f3a094,0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]',
  warn: '!shadow-[inset_4px_0_0_#8a6212,0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:!shadow-[inset_4px_0_0_#dcbb72,0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]',
  redEd: '!border-casevo-mid !shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),inset_4px_0_0_#c23b2c,0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),inset_4px_0_0_#f3a094,0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]',
  warnEd: '!border-casevo-mid !shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),inset_4px_0_0_#8a6212,0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:!shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent),inset_4px_0_0_#dcbb72,0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]',
}
const accentCls = (k: Kind, editing: boolean): string => {
  if (k === 'overdue') return editing ? ACCENT.redEd : ACCENT.red
  if (k === 'soon' || k === 'today') return editing ? ACCENT.warnEd : ACCENT.warn
  return editing ? CARD_EDITING : ''
}
const TILE_CLS: Record<Kind, string> = {
  overdue: 'bg-[#fbe9e6] dark:bg-[rgba(243,160,148,0.14)] [&_*]:!text-[#c23b2c] dark:[&_*]:!text-[#f3a094]',
  today: 'bg-[#fdf3dc] dark:bg-[rgba(214,170,80,0.14)] [&_*]:!text-[#8a6212] dark:[&_*]:!text-[#dcbb72]',
  soon: 'bg-[#fdf3dc] dark:bg-[rgba(214,170,80,0.14)] [&_*]:!text-[#8a6212] dark:[&_*]:!text-[#dcbb72]',
  later: 'bg-casevo-input',
  done: 'bg-casevo-input',
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const longDay = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number)
  return `${DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTH[m - 1]} ${y}`
}
const shortDay = (ymd: string): string => {
  const [, m, d] = ymd.split('-').map(Number)
  return `${d} ${MON[m - 1]}`
}
// Single-line note summary (mockup .dnotes) — also the live view's typography.
const NOTE_LINE =
  'text-[13.5px] leading-[1.45] text-casevo-text-soft truncate max-w-[620px] [&_p]:inline [&_p]:mr-1.5 [&_ul]:inline [&_ol]:inline [&_li]:inline [&_li]:mr-1.5 [&_.ProseMirror]:truncate'

// ══════════════════════════════════════════════════════════════
// One deadline card (view / edit) — mirrors the Witness card.
// ══════════════════════════════════════════════════════════════
interface DeadlineCardProps {
  // For the limitation checks on limitation-type rows.
  accidentYmd?: string | null
  clientDob?: DobInput
  deadline: CaseDeadline
  today: string
  isDeleting: boolean
  collabUser: ReturnType<typeof useCollabUser>
  startEditing?: boolean
  onDone?: () => void
  onFieldChange: (deadline: CaseDeadline, changes: UpdateCaseDeadlinePayload) => void
  onNotesSnapshot: (deadlineId: string, html: string) => void
  onToggleCompleted: (deadline: CaseDeadline) => void
  onRemove: (deadline: CaseDeadline) => void
  onOpenCalendar: (deadline: CaseDeadline) => void
  // The case's claim facts — the "later than Casevo's estimate" check.
  claimFacts?: LimitationClaimFacts
}
const DeadlineCard: React.FC<DeadlineCardProps> = ({
  deadline, today, isDeleting, collabUser, startEditing, onDone, onFieldChange, onNotesSnapshot, onToggleCompleted, onRemove, onOpenCalendar, accidentYmd, clientDob, claimFacts,
}) => {
  const d = deadline
  // A limitation-type row gets the shared limitation checks (error: before
  // the accident; amber: later than Casevo's estimate).
  const limCheck: LimitationCheck = isLimitationName(d.deadlineType?.name) && !d.completed ? limitationDateCheck(d.dueDate?.slice(0, 10), accidentYmd, clientDob, claimFacts) : null
  const [editing, setEditing] = useState(!!startEditing)
  const [snapDate, setSnapDate] = useState<string | null>(startEditing ? (d.dueDate ? d.dueDate.slice(0, 10) : null) : null)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent): void => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false) }
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.stopPropagation(); setMenuOpen(false) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true) }
  }, [menuOpen])

  // The server refused a due date (e.g. a limitation date before the
  // accident): the row went back to the saved date; its reason shows here.
  const refusedDue = useFieldRefusal(deadlineScope(d.id), 'dueDate')
  const k = kindOf(d, today)
  const ymd = ymdOf(d.dueDate)
  // Pull YYYY-MM-DD from the backend's dueDate for DateInput.
  const dateForInput = d.dueDate ? d.dueDate.slice(0, 10) : null
  const docName = `deadline:${d.id}:notes`
  const rel = k === 'done'
    ? 'Completed'
    : ymd ? relDays(daysBetween(today, ymd)) : ''
  const [, tm, td] = (ymd ?? '1970-01-01').split('-').map(Number)
  const ty = (ymd ?? '').slice(0, 4)

  const open = (): void => { setSnapDate(dateForInput); setMenuOpen(false); setSaveFailed(false); setEditing(true) }
  const close = (): void => { setEditing(false); setSaveFailed(false); onDone?.() }
  // Cancel restores the due date (notes are a live document) through the same
  // per-row path + a programmatic broadcast.
  const cancel = (): void => {
    if (snapDate && snapDate !== dateForInput) {
      onFieldChange(d, { dueDate: snapDate })
      broadcastFieldEdits([`deadline.${d.id}.due-date`])
    }
    close()
  }
  const save = async (): Promise<void> => {
    setSaving(true)
    setSaveFailed(false)
    // Notes snapshot first (→ scheduleDeadlineSave), so Save persists it NOW.
    try { await flushLiveDocSnapshots(); await flushDeadlineAutoSave() } finally { setSaving(false) }
    if (saveDidFail()) { setSaveFailed(true); return }
    // A refused due date stays open so its reason (under the field) is seen.
    if (getFieldRefusal(deadlineScope(d.id), 'dueDate')) return
    close()
  }

  return (
    <section aria-label={d.deadlineType.name} className={`${CARD} group/card flex flex-col transition-[border-color,box-shadow] ${accentCls(k, editing)} ${k === 'done' && !editing ? 'opacity-60' : ''} ${editing ? 'overflow-hidden' : ''}`}>
      <div className="flex items-center gap-[14px] py-3 px-[14px]">
        {/* Complete tick — the immediate PATCH (handleToggleCompleted). */}
        <button
          type="button"
          onClick={() => onToggleCompleted(d)}
          disabled={isDeleting}
          aria-pressed={d.completed}
          aria-label={d.completed ? 'Mark deadline as not completed' : 'Mark deadline as completed'}
          title={d.completed ? 'Mark not completed' : 'Mark completed'}
          className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 transition-colors focus:outline-none casevo-focus disabled:opacity-60 ${d.completed ? 'bg-casevo-primary border-casevo-primary text-white' : 'border-casevo-border hover:border-casevo-mid'}`}
        >
          {d.completed && <KIcon name="check" size={12} strokeWidth={3.5} />}
        </button>
        {/* Date tile (mockup .dtile) */}
        <div className={`w-[50px] shrink-0 text-center rounded-[10px] pt-[5px] pb-[6px] leading-[1.1] ${TILE_CLS[k]}`} aria-hidden="true">
          <span className="block text-[10px] font-bold uppercase tracking-[0.05em] text-casevo-text-soft">{ymd ? MON[tm - 1] : '—'}</span>
          <b className="block text-[19px] text-casevo-forest my-px">{ymd ? String(td).padStart(2, '0') : ''}</b>
          <span className="block text-[10px] font-bold uppercase tracking-[0.05em] text-casevo-text-soft">{ty}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[9px] flex-wrap">
            <span className={`text-[15px] font-semibold text-casevo-forest ${k === 'done' ? 'line-through' : ''}`}>{d.deadlineType.name}</span>
            <span className={`text-[11px] font-bold px-2 py-[2px] rounded-full whitespace-nowrap ${CHIP_CLS[k]}`}>{CHIP_LABEL[k]}</span>
            <span className={`text-[12.5px] ${k === 'overdue' ? 'text-[#c23b2c] dark:text-[#f3a094] font-semibold' : 'text-casevo-text-soft'}`}>{ymd ? `${fmtDay(ymd)} · ${rel}` : rel}</span>
          </div>
          {limCheck && (
            <div role={limCheck.kind === 'error' ? 'alert' : 'status'} className={`mt-[4px] text-[12.5px] font-medium ${limCheck.kind === 'error' ? 'text-casevo-danger' : 'text-casevo-warn'}`}>{limCheck.message}</div>
          )}
          {!editing && (
            <div className="mt-[3px] min-w-0">
              {/* LIVE read-only one-line notes (saved copy until synced). */}
              <LiveDocView
                docName={docName}
                html={d.notes ?? ''}
                onSnapshot={(html) => onNotesSnapshot(d.id, html)}
                collabUser={collabUser}
                proseClassName={NOTE_LINE}
                empty={<span className="text-casevo-text-soft italic text-[13.5px]">No notes</span>}
              />
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-[2px] self-start">
          <CardPresence match={(key) => key.startsWith(`deadline.${d.id}.`)} className="mr-1" />
          {editing ? (
            <span className="text-[12.5px] text-casevo-text-soft">Editing</span>
          ) : (
            <>
              <button
                type="button"
                onClick={open}
                disabled={isDeleting}
                title="Edit deadline"
                aria-label={`Edit ${d.deadlineType.name} deadline`}
                className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100"
              >
                {IcEdit(15)}
              </button>
              <div ref={menuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="Deadline actions"
                  className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-input transition-colors focus:outline-none casevo-focus"
                >
                  <KIcon name="dots" size={15} />
                </button>
                {menuOpen && (
                  <div role="menu" className={`${MENU_SURFACE} !w-[196px] absolute right-0 top-[34px] z-20`}>
                    <MenuItem icon={<MenuIcon name="edit" />} label="Edit deadline" onClick={open} />
                    <MenuItem icon={<MenuIcon name="calendar" />} label="Open in calendar" onClick={() => { setMenuOpen(false); onOpenCalendar(d) }} />
                    <MenuItem icon={<MenuIcon name="check" />} label={d.completed ? 'Mark not completed' : 'Mark completed'} onClick={() => { setMenuOpen(false); onToggleCompleted(d) }} />
                    <MenuSep />
                    <MenuItem icon={<MenuIcon name="trash" />} label={isDeleting ? 'Deleting…' : 'Delete deadline'} tone="danger" disabled={isDeleting} onClick={() => { setMenuOpen(false); onRemove(d) }} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {editing && (
        <>
          <div className="pt-1 px-[18px] pb-4">
            <div className={FGRID}>
              <FormField variant="info" label="Type">
                {/* Type is fixed once created (UpdateCaseDeadlinePayload has no type). */}
                <div className="h-[38px] rounded-[9px] border border-dashed border-casevo-border px-[11px] flex items-center gap-2 text-[14px] text-casevo-text-soft min-w-0">
                  <KIcon name="flag" size={15} /><span className="truncate">{d.deadlineType.name} · set when the deadline is created</span>
                </div>
              </FormField>
              <FormField variant="info" label="Due date" fieldKey={`deadline.${d.id}.due-date`} required>
                <DateInput
                  value={dateForInput}
                  onChange={(v) => {
                    if (v) onFieldChange(d, { dueDate: v })
                  }}
                  ariaLabel="Due date"
                  allowFuture
                  fieldClassName={`h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface ${refusedDue ? 'border-casevo-danger' : 'border-casevo-border'}`}
                />
                <FieldError message={refusedDue ?? undefined} />
              </FormField>
              {/* Notes — Yjs collaborative rich-text, PER ROW. Doc
                  `deadline:<deadlineId>:notes` (unique per deadline), keyed by
                  the doc name. Same props as before. */}
              <div className="sm:col-span-2">
                <FormField variant="info" label="Notes" fieldKey={`deadline.${d.id}.notes`}>
                  <LiveDocEdit>
                    {(onStatus) => (
                      <RichTextEditor
                        key={docName}
                        collabDocName={docName}
                        collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                        initialHtml={DOMPurify.sanitize(d.notes ?? '')}
                        onSnapshot={(html) => onNotesSnapshot(d.id, html)}
                        placeholder="Notes…"
                        collabUser={collabUser}
                        onCollabStatus={onStatus}
                      />
                    )}
                  </LiveDocEdit>
                </FormField>
              </div>
            </div>
            <LiveNote>The notes are a live document: everyone sees each other’s typing, and they aren’t part of Cancel.</LiveNote>
          </div>
          <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
            <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
              <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />Saves as you type · Cancel undoes the date
            </span>
            {saveFailed && <span role="alert" className="text-[12px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
            <button type="button" className={`${BTN_SEC} ${SM} !text-casevo-danger`} onClick={() => onRemove(d)} disabled={isDeleting || saving}>
              <MenuIcon name="trash" size={13} />{isDeleting ? 'Deleting…' : 'Delete'}
            </button>
            <button type="button" className={`${BTN_SEC} ${SM}`} onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save() }} disabled={saving}>{IcCheck(13)}{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      )}
    </section>
  )
}

// Group header (mockup .dgrp).
const GroupHead: React.FC<{ label: string; count: number; extra?: React.ReactNode }> = ({ label, count, extra }) => (
  <div className="flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase text-casevo-text-soft mt-[6px] mx-[2px] -mb-1">
    {label}
    <span className="text-[11px] tracking-normal px-[7px] py-px rounded-full bg-casevo-border-soft">{count}</span>
    {extra}
  </div>
)

// A sentence ends once (a reason may already end in . ! or ?).
const withStop = (t: string): string => (/[.!?]$/.test(t.trim()) ? t.trim() : `${t.trim()}.`)

// How the estimate was worked out, per basis (the limitation box, B2).
const ESTIMATE_WORDING: Record<LimitationBasis, string> = {
  adult: 'the accident date + 3 years',
  child: '3 years after the client’s 18th birthday (they were under 18 at the accident)',
  noDob: 'the accident date + 3 years. There’s no date of birth, so a child’s later date can’t be worked out',
  override: 'the own date',
  cica: 'the incident date + 2 years (criminal injuries)',
  holiday: 'the accident date + 2 years (air / sea travel; no arrival date yet)',
  fatal: 'the date of death + 3 years',
  holidayNoRegime: 'the accident date + 1 year (the shortest period that may apply until the type of holiday claim is chosen)',
  travel: 'the arrival (or disembarkation) date + 2 years (air / sea travel)',
  foreignLaw: 'the accident date + the foreign limitation period',
  mib: 'the accident date + 3 years (untraced driver, MIB — no extension for children)',
  cicaChild: 'the day before the client’s 20th birthday (CICA: reported to police before they turned 18)',
  cicaReport: 'the first police report + 2 years (CICA: reported after they turned 18)',
  // (shown with only the dates that are set — limitationRules diseaseWording)
  disease: 'the earliest disease date + 3 years',
  militaryCap: 'the overseas operations longstop (6 years)',
}

const DeadlinesSection: React.FC = () => {
  const { currentCase, caseDeadlines, addCaseDeadline, updateCaseDeadline, removeCaseDeadline } =
    useCasevoStore()

  // Deadline types — fetched once on mount into local state.
  const [deadlineTypes, setDeadlineTypes] = useState<DeadlineType[]>([])
  const [typesError, setTypesError] = useState<string | null>(null)
  // true until the fetch resolves OR rejects (loading vs "firm has no types").
  const [typesLoading, setTypesLoading] = useState(true)

  // New-deadline card state.
  const [addOpen, setAddOpen] = useState(false)
  const [formTypeId, setFormTypeId] = useState<string>('')
  const [formDueDate, setFormDueDate] = useState<string | null>(null)
  // false while the due date is half-typed — Add waits (DateInput onValidityChange).
  const [formDueValid, setFormDueValid] = useState(true)
  const [formNotes, setFormNotes] = useState<string>('')
  const [prefilledFromCalendar, setPrefilledFromCalendar] = useState(false)
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)

  // Per-row in-flight state — keyed by deadlineId.
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteDeadline, setConfirmDeleteDeadline] = useState<CaseDeadline | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showCompleted, setShowCompleted] = useState(true)
  const [limEditing, setLimEditing] = useState(false)
  // ★ The required-fields row's "Limitation date" chip → here, focusing the
  // "No limitation date" card's Add button (a card with nothing to open).
  useFieldFocusRequest(
    (field) => (field === LIMITATION_FIELD_KEY ? 'limitation' : null),
    () => {},
    () => true,
    () => 'section[aria-label="No limitation date"]',
  )
  // ★ (Limitation audit) One-click add of an estimate that is ALREADY PAST must
  // be confirmed — the claim may be statute-barred (or the accident date wrong).
  const [confirmPastLim, setConfirmPastLim] = useState<{ typeId: string; ymd: string } | null>(null)
  // Collab identity for the Yjs carets on the per-row notes editors.
  const collabUser = useCollabUser()

  // Calendar "New deadline on this day" pre-fill: seed the due date, open the
  // New deadline card, clear the slot so it doesn't leak to the next case.
  const pendingNewDeadlineDueDate = useCasevoStore((s) => s.pendingNewDeadlineDueDate)
  const setPendingNewDeadlineDueDate = useCasevoStore((s) => s.setPendingNewDeadlineDueDate)
  useEffect(() => {
    if (!pendingNewDeadlineDueDate) return
    setFormDueDate(pendingNewDeadlineDueDate)
    setPrefilledFromCalendar(true)
    setAddOpen(true)
    setPendingNewDeadlineDueDate(null)
  }, [pendingNewDeadlineDueDate, setPendingNewDeadlineDueDate])

  // Fetch the firm's deadline types on mount — and again when an admin
  // changes them (D12 live updates: firm-changed ['deadlineTypes']).
  const [typesRev, setTypesRev] = useState(0)
  useFirmChange(['deadlineTypes'], () => setTypesRev((n) => n + 1))
  useEffect(() => {
    let cancelled = false
    void listDeadlineTypesApi().then((types) => {
      if (cancelled) return
      setDeadlineTypes(types)
    }).catch((err) => {
      if (cancelled) return
      // eslint-disable-next-line no-console
      console.error('[Deadlines] Failed to load deadline types:', err)
      setTypesError(err instanceof Error ? err.message : 'Failed to load deadline types')
    }).finally(() => {
      if (!cancelled) setTypesLoading(false)
    })
    return () => { cancelled = true }
  }, [typesRev])

  if (!currentCase) return null
  const list: CaseDeadline[] = caseDeadlines[currentCase.id] ?? []
  // ★ (Limitation audit) Not loaded yet (or the load failed) ≠ "no deadlines":
  // never show the "No limitation date" warning / one-click add from an
  // unloaded list — it read as missing and offered a duplicate (and, after a
  // failed load, an estimate that may be LATER than the real saved date).
  const deadlinesLoaded = caseDeadlines[currentCase.id] !== undefined
  const today = todayYmd()

  // ── CREATE (shared by the New deadline card + the one-click limitation) ──
  const createDeadline = async (deadlineTypeId: string, dueDate: string, notes: string): Promise<boolean> => {
    setAddError(null)
    setAdding(true)
    try {
      const created = await createCaseDeadlineApi(currentCase.id, {
        deadlineTypeId,
        dueDate,
        notes: notes.trim() || undefined,
      })
      addCaseDeadline(currentCase.id, created)
      // Capture the new row's server updatedAt + column VALUES so an edit right
      // after creation is guarded AND the column-aware 409 recovery has a base.
      setDeadlineBaseUpdatedAt(created.id, created.updatedAt)
      setDeadlineBaseValues(created.id, created as unknown as Record<string, unknown>)
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add deadline'
      setAddError(message)
      return false
    } finally {
      setAdding(false)
    }
  }
  const handleAdd = async (): Promise<void> => {
    setAddError(null)
    if (!formTypeId) { setAddError('Pick a deadline type.'); return }
    if (!formDueDate) { setAddError('Pick a due date.'); return }
    if (!formDueValid) { setAddError('Finish the due date or clear it.'); return }
    const ok = await createDeadline(formTypeId, formDueDate, formNotes)
    if (ok) closeAdd()
  }
  const openAdd = (typeId = ''): void => {
    setAddError(null)
    setFormTypeId(typeId)
    setAddOpen(true)
  }
  const closeAdd = (): void => {
    setAddOpen(false)
    setFormTypeId('')
    setFormDueDate(null)
    setFormDueValid(true)
    setFormNotes('')
    setPrefilledFromCalendar(false)
    setAddError(null)
  }

  // ── PER-ROW HANDLERS (unchanged logic) ────────────────────
  // Field edit — optimistic store update then debounced PATCH.
  const handleFieldChange = (
    deadline: CaseDeadline,
    changes: UpdateCaseDeadlinePayload,
  ): void => {
    const next: CaseDeadline = {
      ...deadline,
      ...(changes.dueDate !== undefined ? { dueDate: changes.dueDate } : {}),
      ...(changes.completed !== undefined ? { completed: changes.completed } : {}),
      ...(changes.notes !== undefined ? { notes: changes.notes } : {}),
    }
    updateCaseDeadline(next)
    scheduleDeadlineSave(deadline.id, changes)
  }
  // Notes HTML-mirror snapshot (Yjs) via the SAME per-row autosave, reading
  // the FRESHEST row from the store.
  const handleNotesSnapshot = (deadlineId: string, html: string): void => {
    const store = useCasevoStore.getState()
    const row = (store.caseDeadlines[currentCase.id] ?? []).find((d) => d.id === deadlineId)
    if (!row) return
    store.updateCaseDeadline({ ...row, notes: html })
    scheduleDeadlineSave(deadlineId, { notes: html })
  }
  // Toggle completed — an IMMEDIATE PATCH that drains + merges any pending
  // date/notes payload for the row; base + 409 handling; rollback on failure.
  const handleToggleCompleted = async (deadline: CaseDeadline): Promise<void> => {
    const next = !deadline.completed
    const drained = consumePendingDeadlinePayload(deadline.id) ?? {}
    const base = getDeadlineBaseUpdatedAt(deadline.id)
    const body = { ...drained, completed: next, ...(base !== null ? { baseUpdatedAt: base } : {}) }
    const optimistic: CaseDeadline = {
      ...deadline,
      ...(body.dueDate !== undefined ? { dueDate: body.dueDate } : {}),
      ...(body.notes !== undefined ? { notes: body.notes } : {}),
      completed: next,
    }
    updateCaseDeadline(optimistic)
    try {
      const saved = await updateCaseDeadlineApi(deadline.id, body)
      updateCaseDeadline(saved)
      setDeadlineBaseUpdatedAt(deadline.id, saved.updatedAt)
      setDeadlineBaseValues(deadline.id, saved as unknown as Record<string, unknown>)
    } catch (err) {
      updateCaseDeadline(deadline)
      const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
      if (resp?.status === 409) {
        if (resp.data?.currentUpdatedAt) setDeadlineBaseUpdatedAt(deadline.id, resp.data.currentUpdatedAt)
        useCasevoStore.getState().setSaveConflict(
          "A deadline was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
        )
        return
      }
      // Re-queue the drained date/notes edits (still shown after rollback).
      if (Object.keys(drained).length > 0) scheduleDeadlineSave(deadline.id, drained)
      // eslint-disable-next-line no-console
      console.error('[Deadlines] Failed to update completed status:', err)
      setActionError(refusalText(err, 'Could not update deadline. Please try again.'))
    }
  }
  const handleRemove = (deadline: CaseDeadline): void => {
    setConfirmDeleteDeadline(deadline)
  }
  // Confirmed delete — cancel the row's pending PATCH first.
  const handleConfirmDelete = async (): Promise<void> => {
    const deadline = confirmDeleteDeadline
    if (!deadline) return
    setConfirmDeleteDeadline(null)
    setActionError(null)
    setDeletingId(deadline.id)
    cancelDeadlineSave(deadline.id)
    try {
      await deleteCaseDeadlineApi(deadline.id)
      removeCaseDeadline(currentCase.id, deadline.id)
      if (limRow && deadline.id === limRow.id) setLimEditing(false)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Deadlines] Failed to delete deadline:', err)
      setActionError(refusalText(err, 'Could not remove deadline. Please try again.'))
    } finally {
      setDeletingId(null)
    }
  }
  // Open the calendar on a day — persist pending edits first.
  const openCalendar = async (ymd: string | null): Promise<void> => {
    await flushLiveDocSnapshots()
    await Promise.all([flushAutoSave(), flushDeadlineAutoSave()])
    // One history step (Back returns to this case's Deadlines).
    await navigateTo({ view: 'calendar' }, { skipFlush: true, intents: (s) => { if (ymd) s.setPendingCalendarDate(ymd) } })
  }

  // ── derived ──
  const lim = limitationOf(currentCase, list)
  // The estimate (no limitation deadline saved): THE rule, incl. a child's
  // 18th birthday + 3 years — what the one-click add saves.
  const limEst = lim && lim.estimated ? lim : null
  // The same row limitationOf reads (open first, then earliest) — any other
  // limitation-type rows stay in the groups below.
  const limRow = limitationRowOf(list)
  // Checks shown under a SAVED Limitation deadline (caseFacts.limitationChecksOf).
  const limChecks = limitationChecksOf(currentCase)
  // (B2b) Child sexual abuse confirmed as having no time limit (s.11ZA) — the
  // box says so instead of "No limitation date" (caseFacts.noTimeLimitOf).
  const noLimit = !limRow ? noTimeLimitOf(currentCase) : null
  // (B2b fix 2) The date (saved, or the estimate) is before today.
  const limPassed = limitationPassedOf(currentCase, list, today)
  const limType = deadlineTypes.find((t) => LIMITATION_RE.test(t.name)) ?? null
  // Shared date rule (ISO or UK D/M/YYYY) — the same reading as the estimate.
  const accYmd = textDateToIso(currentCase.accidentDetails.accidentDate)
  const rows = list
    .filter((d) => d.id !== limRow?.id)
    .slice()
    .sort((a, b) => (ymdOf(a.dueDate) ?? '').localeCompare(ymdOf(b.dueDate) ?? ''))
  const overdue = rows.filter((d) => kindOf(d, today) === 'overdue')
  const soon = rows.filter((d) => { const k = kindOf(d, today); return k === 'soon' || k === 'today' })
  const later = rows.filter((d) => kindOf(d, today) === 'later')
  const done = rows.filter((d) => d.completed)
  const assignee = currentCase.assignedTo ? `${currentCase.assignedTo.firstName} ${currentCase.assignedTo.lastName}`.trim() : null

  const cardProps = {
    accidentYmd: accYmd,
    clientDob: caseDobOf(currentCase.clientDetails?.dateOfBirth, currentCase.client?.dateOfBirth).limitationDob,
    claimFacts: claimFactsOf(currentCase.claim, currentCase.protectedParty),
    today, collabUser,
    onFieldChange: handleFieldChange,
    onNotesSnapshot: handleNotesSnapshot,
    onToggleCompleted: (d: CaseDeadline) => { void handleToggleCompleted(d) },
    onRemove: handleRemove,
    onOpenCalendar: (d: CaseDeadline) => { void openCalendar(ymdOf(d.dueDate)) },
  }
  const renderRow = (d: CaseDeadline): React.ReactNode => (
    <DeadlineCard key={d.id} deadline={d} isDeleting={deletingId === d.id} {...cardProps} />
  )

  // ── limitation card (mockup .limc) ──
  const limDays = lim && !lim.estimated ? daysBetween(today, lim.ymd) : null
  const startYmd = accYmd ?? ymdOf(currentCase.createdAt)
  const pct = lim && startYmd ? Math.max(0, Math.min(100, (daysBetween(startYmd, today) / Math.max(1, daysBetween(startYmd, lim.ymd))) * 100)) : 0
  const statutory3y = accYmd ? addYearsCivil(accYmd, 3) : null
  const isStatutory3y = !!(lim && statutory3y === lim.ymd)
  // ★ A SET limitation date LATER than accident + 3 years (e.g. the accident
  // date was corrected to an earlier day after the deadline was added) is the
  // dangerous direction — flag it so a fee earner checks it. Never auto-changed:
  // a later date can be right (date of knowledge, a minor, an agreed standstill).
  // ★ The shared limitation checks (utils/limitationRules): before the accident
  // (blocked by the server), a child at the accident and the date before their
  // 21st birthday, or an adult's date that isn't accident + 3 years — amber,
  // never auto-changed (a different date can be right: date of knowledge…).
  // THE DOB this case uses (utils/caseDob): its own copy, else the client record.
  const clientDob = caseDobOf(currentCase.clientDetails?.dateOfBirth, currentCase.client?.dateOfBirth).limitationDob
  // The box shows only the ERROR here (before the accident); "later than
  // Casevo's estimate" goes at the TOP of Checks (B2b fix), amber.
  const limCheckAll: LimitationCheck = lim && !lim.estimated ? limitationDateCheck(lim.ymd, accYmd, clientDob, claimFactsOf(currentCase.claim, currentCase.protectedParty)) : null
  const limCheck: LimitationCheck = limCheckAll?.kind === 'error' ? limCheckAll : null
  const laterCheck = limitationLaterCheckOf(currentCase, list)
  const checks = [...(laterCheck ? [laterCheck.message] : []), ...limChecks]
  const limNote = limRow ? htmlText(limRow.notes) : ''
  const LIM_TITLE = 'text-[20px] font-bold tracking-[-0.01em] mt-[2px] flex items-center gap-2 text-casevo-forest'
  const LIM_SUB = 'text-[12.5px] text-casevo-text-soft mt-[2px] max-w-[560px]'
  const limitationCard = limRow && !limEditing ? (
    <section aria-label="Limitation date" className={`${CARD} group/card !border-[color-mix(in_srgb,var(--casevo-brand)_45%,var(--casevo-border))] overflow-visible`}>
      <div className="flex items-center gap-4 py-4 px-[18px] flex-wrap">
        <span className="w-11 h-11 rounded-[12px] bg-casevo-primary text-white grid place-items-center shrink-0" aria-hidden="true"><KIcon name="scale" size={22} /></span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-brand dark:text-[#8fd6b0]">Limitation date · statutory</div>
          <div className={LIM_TITLE}>
            {lim ? longDay(lim.ymd) : '—'}
            {limRow.completed && <span className={`text-[11px] font-bold px-2 py-[2px] rounded-full ${CHIP_CLS.done}`}>Done</span>}
          </div>
          <div className={LIM_SUB}>
            {isStatutory3y && accYmd ? `3 years from the accident (${fmtDay(accYmd)}) · Limitation Act 1980, s.11` : 'Set on this case as a “Limitation date” deadline'}
          </div>
          {/* (B2b fix 2) red, calm: the saved date is before today */}
          {limPassed && <div><div role="status" className="mt-[7px] inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-[9px] py-[3px] rounded-[8px] bg-casevo-danger-bg text-casevo-danger"><KIcon name="alert" size={13} />{LIMITATION_PASSED}</div></div>}
          {limCheck && (
            <div role="alert" className={`mt-[7px] inline-flex items-start gap-1.5 text-[12.5px] font-semibold px-[9px] py-[3px] rounded-[8px] ${limCheck.kind === 'error' ? 'bg-casevo-danger-bg text-casevo-danger' : WARN}`}>
              <KIcon name="alert" size={13} />
              {limCheck.message}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-[6px]">
          {limDays !== null && !limRow.completed && (
            <div className="text-right mr-2">
              <b className={`block text-[24px] leading-none font-bold tabular-nums ${limDays < 0 ? 'text-[#c23b2c] dark:text-[#f3a094]' : 'text-casevo-forest'}`}>{Math.abs(limDays).toLocaleString('en-GB')}</b>
              <span className="text-[11.5px] text-casevo-text-soft">{limDays < 0 ? 'days passed' : limDays === 1 ? 'day left' : 'days left'}</span>
            </div>
          )}
          <CardPresence match={(key) => key.startsWith(`deadline.${limRow.id}.`)} className="mr-1" />
          <button
            type="button"
            onClick={() => setLimEditing(true)}
            title="Edit limitation date"
            aria-label="Edit limitation date"
            className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] transition-colors focus:outline-none casevo-focus"
          >
            {IcEdit(15)}
          </button>
          <button
            type="button"
            onClick={() => { void openCalendar(lim?.ymd ?? null) }}
            title="Open in calendar"
            aria-label="Open the limitation date in the calendar"
            className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-input transition-colors focus:outline-none casevo-focus"
          >
            <KIcon name="cal" size={15} />
          </button>
        </div>
      </div>
      <div className="h-[5px] bg-casevo-border-soft mx-[18px] rounded-[3px] overflow-hidden" aria-hidden="true">
        <i className="block h-full bg-casevo-brand rounded-[3px]" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-[18px] gap-y-[6px] pt-[11px] px-[18px] pb-[13px] text-[12px] text-casevo-text-soft">
        <span className="inline-flex items-center gap-1.5"><span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex"><KIcon name="check" size={13} /></span>Shown in the case hero, the Overview and the quick view</span>
        <span className="inline-flex items-center gap-1.5"><span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex"><KIcon name="bell" size={13} /></span>Daily scan alerts the case team within 3 days of it and on the day</span>
        {limNote && <span className="block ml-auto text-casevo-forest max-w-full truncate"><b className="font-bold">Notes:</b> {limNote}</span>}
      </div>
      {/* ★ (Audit M6) The estimate's checks still apply once a Limitation
          deadline is saved (internal guidance, never client-facing). */}
      {checks.length > 0 && (
        <div className="px-[18px] pb-[13px] -mt-[2px] border-t border-casevo-border-soft pt-[10px]">
          <h4 className="m-0 text-[11px] font-bold tracking-[0.14em] uppercase text-[#8a6212] dark:text-[#dcbb72]">Checks</h4>
          <ul aria-label="Limitation checks" className="mt-[6px] mb-0 pl-0 list-none space-y-[4px]">
            {checks.map((n) => (
              <li key={n} className="flex items-start gap-[7px] text-[12.5px] leading-[1.45] text-[#8a6212] dark:text-[#dcbb72]">
                <span className="inline-flex mt-[2px]"><KIcon name="alert" size={13} /></span><span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  ) : limRow && limEditing ? (
    // Editing the limitation deadline = the standard deadline edit card.
    // key = the row id: if the limitation row changes while editing (another
    // limitation-type row becomes "the" one), remount so the edit card's
    // Cancel snapshot can never be restored onto a different deadline.
    <DeadlineCard key={limRow.id} deadline={limRow} isDeleting={deletingId === limRow.id} startEditing onDone={() => setLimEditing(false)} {...cardProps} />
  ) : noLimit ? (
    // (B2b) NO TIME LIMIT — confirmed in the Claim card (s.11ZA).
    <section aria-label="No time limit" className={`${CARD} !border-[color-mix(in_srgb,var(--casevo-brand)_45%,var(--casevo-border))]`}>
      <div className="flex items-center gap-4 py-4 px-[18px] flex-wrap">
        <span className="w-11 h-11 rounded-[12px] bg-casevo-primary text-white grid place-items-center shrink-0" aria-hidden="true"><KIcon name="scale" size={22} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-casevo-brand dark:text-[#8fd6b0]">Limitation · child sexual abuse</div>
          <div className={LIM_TITLE}>No time limit</div>
          <div className={LIM_SUB}>{confirmedLine(currentCase.abuseNoLimitConfirmedBy, noLimit.confirmedAt)}</div>
          <div className={`${LIM_SUB} mt-[6px]`}>Remove the confirmation in Case control → Claim if it no longer applies.</div>
        </div>
        <div className="ml-auto flex items-center gap-[6px]">
          <button type="button" className={BTN_SEC} disabled={!limType} title={!limType ? 'Add a “Limitation date” type in Admin → Deadline types first' : undefined} onClick={() => openAdd(limType?.id ?? '')}>
            <KIcon name="plus" size={15} />Add a limitation date anyway
          </button>
        </div>
      </div>
    </section>
  ) : (
    // MISSING — dashed amber warning + one-click add.
    <section aria-label="No limitation date" className={`${CARD} !border-dashed !border-[#8a6212] dark:!border-[#dcbb72] !bg-[color-mix(in_srgb,#fdf3dc_55%,var(--casevo-surface))] dark:!bg-[color-mix(in_srgb,rgba(214,170,80,0.14)_55%,var(--casevo-surface))]`}>
      <div className="flex items-center gap-4 py-4 px-[18px] flex-wrap">
        <span className={`w-11 h-11 rounded-[12px] grid place-items-center shrink-0 ${WARN}`} aria-hidden="true"><KIcon name="alert" size={22} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#8a6212] dark:text-[#dcbb72]">No limitation date on this case</div>
          <div className={LIM_TITLE}>
            {limEst ? <>{limEst.basis === 'override' ? '' : 'Estimated '}{longDay(limEst.ymd)} <span className="whitespace-nowrap text-[10.5px] font-bold tracking-[0.06em] uppercase px-[6px] py-px rounded-[4px] border border-[#8a6212] text-[#8a6212] dark:border-[#dcbb72] dark:text-[#dcbb72]">{estimateBadge(limEst.basis)}</span></> : 'Not set'}
          </div>
          <div className={LIM_SUB}>
            {limEst
              ? <>{limEst.basis === 'override'
                  ? <>{currentCase.limitationOverrideSetBy ? `Own date set by ${currentCase.limitationOverrideSetBy.name}: ` : 'Own date: '}{withStop(currentCase.claim?.limitationOverrideReason || 'no reason recorded')}</>
                  : <>Worked out as {limEst.basis === 'child' ? childWording(estimateStartOf(accYmd, claimFactsOf(currentCase.claim, currentCase.protectedParty))) : limEst.basis === 'disease' ? diseaseWording(accYmd, claimFactsOf(currentCase.claim, currentCase.protectedParty)) : ESTIMATE_WORDING[limEst.basis]}.</>} It shows until a “Limitation date” deadline is added. {limEst.basis === 'override' ? 'It' : 'The estimate'} isn’t scanned, so <b className="font-semibold text-casevo-forest">no alerts fire</b>.</>
              : <>{noEstimateHint(currentCase.claim?.claimType)}, or add the limitation date as a deadline. Without it, <b className="font-semibold text-casevo-forest">no alerts fire</b>.</>}
          </div>
          {/* (B2b fix 2) red, calm: the estimate / own date is before today */}
          {limPassed && limEst && <div><div role="status" className="mt-[7px] inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-[9px] py-[3px] rounded-[8px] bg-casevo-danger-bg text-casevo-danger"><KIcon name="alert" size={13} />{LIMITATION_PASSED}</div></div>}
          {/* B2: the estimate's internal guidance (never client-facing). */}
          {limEst && limEst.notes.length > 0 && (
            <ul aria-label="Limitation notes" className="mt-[8px] mb-0 pl-0 list-none space-y-[4px]">
              {limEst.notes.map((n) => (
                <li key={n} className="flex items-start gap-[7px] text-[12.5px] leading-[1.45] text-[#8a6212] dark:text-[#dcbb72]">
                  <span className="inline-flex mt-[2px]"><KIcon name="alert" size={13} /></span><span>{n}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="ml-auto flex items-center gap-[6px]">
          {lim && limType ? (
            <button type="button" className={BTN_PRI} disabled={adding} onClick={() => {
              if (lim.ymd < today) setConfirmPastLim({ typeId: limType.id, ymd: lim.ymd })
              else void createDeadline(limType.id, lim.ymd, '')
            }}>
              <KIcon name="plus" size={15} />{adding ? 'Adding…' : `Add limitation date · ${fmtDay(lim.ymd)}`}
            </button>
          ) : (
            <button
              type="button"
              className={BTN_PRI}
              disabled={!limType}
              title={!limType ? 'Add a “Limitation date” type in Admin → Deadline types first' : undefined}
              onClick={() => openAdd(limType?.id ?? '')}
            >
              <KIcon name="plus" size={15} />Add limitation date
            </button>
          )}
        </div>
      </div>
      {/* The one-click add has no New deadline card to show its error in. */}
      {addError && !addOpen && <p role="alert" className="text-[12.5px] text-casevo-danger px-[18px] pb-[13px] -mt-1">{addError}</p>}
    </section>
  )

  // ── New deadline card (mockup .newd) ──
  const newCard = addOpen && (
    <section aria-label="New deadline" className={`${CARD} ${CARD_EDITING} overflow-visible flex flex-col`}>
      <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
        <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0"><KIcon name="plus" size={17} /></span>
        <div className="min-w-0">
          <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">New deadline</h3>
          <div className="text-[12px] text-casevo-text-soft mt-px">
            {prefilledFromCalendar && formDueDate ? `Due date prefilled from the calendar (${shortDay(formDueDate)})` : 'Pick a type and a due date'}
          </div>
        </div>
        <span className="ml-auto text-[12.5px] text-casevo-text-soft">Adding</span>
      </div>
      <div className="pt-[2px] px-[18px] pb-4">
        {typesError ? (
          <p className="text-[12.5px] text-casevo-danger">{typesError}</p>
        ) : typesLoading ? (
          <p className="text-[12.5px] text-casevo-text-soft">Loading types…</p>
        ) : deadlineTypes.length === 0 ? (
          <div className={`rounded-[10px] px-[14px] py-[11px] text-[13px] ${WARN}`}>
            No deadline types set up yet. An admin needs to add one (Admin → Deadline Types) before deadlines can be created on a case.
          </div>
        ) : (
          <div className={FGRID}>
            <FormField variant="info" label="Type" required htmlFor="new-deadline-type">
              <select id="new-deadline-type" value={formTypeId} onChange={(e) => setFormTypeId(e.target.value)} disabled={adding} className={`${inpCls(false)} ${SELECT_BG}`}>
                <option value="">Choose a type…</option>
                {deadlineTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <p className="text-[11.5px] text-casevo-text-soft mt-1">Types are set by your firm admin (Admin → Deadline types)</p>
            </FormField>
            <FormField variant="info" label="Due date" required>
              {/* allowFuture: deadline due dates are inherently in the future. */}
              <DateInput
                value={formDueDate}
                onChange={setFormDueDate}
                onValidityChange={setFormDueValid}
                ariaLabel="Due date"
                allowFuture
                fieldClassName="h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface border-casevo-border"
              />
            </FormField>
            <div className="sm:col-span-2">
              <FormField variant="info" label="Notes (optional)" htmlFor="new-deadline-notes">
                <textarea id="new-deadline-notes" value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={2} disabled={adding} className={`${inpCls(false)} !h-auto py-2 resize-y min-h-[38px]`} />
              </FormField>
            </div>
          </div>
        )}
        {addError && <p role="alert" className="text-[12.5px] text-casevo-danger mt-3">{addError}</p>}
      </div>
      <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
        <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
          <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />Created on Add. Notes become a live document afterwards.
        </span>
        <button type="button" className={`${BTN_SEC} ${SM}`} onClick={closeAdd} disabled={adding}>Cancel</button>
        <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void handleAdd() }} disabled={adding || !formTypeId || !formDueDate || !formDueValid}>
          <KIcon name="plus" size={13} />{adding ? 'Adding…' : 'Add deadline'}
        </button>
      </div>
    </section>
  )

  const nextLater = later[0]
  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead
        title="Deadlines"
        sub="Every dated obligation on this case, soonest first. Not part of the setup chain."
        actions={
          <button type="button" className={BTN_SEC} onClick={() => openAdd()} disabled={addOpen}>
            <KIcon name="plus" size={15} />Add deadline
          </button>
        }
      />
      {/* Inline error banner for toggle/delete failures. */}
      {actionError && (
        <div role="alert" className="mb-[14px] flex items-center gap-3 px-[14px] py-[11px] rounded-[12px] border border-[#f5c2bb] bg-[#fbe9e6] text-[#c23b2c] dark:border-[rgba(243,160,148,0.35)] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094] text-[13.5px]">
          <span className="flex-1">{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss error" className="shrink-0 hover:opacity-70 focus:outline-none casevo-focus rounded-[3px]">
            <MenuIcon name="x" size={15} />
          </button>
        </div>
      )}
      <Glance cells={[
        {
          icon: 'scale', label: 'Limitation',
          value: lim ? <span className="tabular-nums">{fmtDay(lim.ymd)}</span> : <GlanceDash />,
          sub: !lim ? 'Not set' : lim.estimated ? (lim.basis === 'noDob' ? estimateBadge(lim.basis) : lim.basis === 'override' ? 'Own date · not alerted' : 'Estimated · not alerted') : limRow?.completed ? 'Completed' : limDays !== null && limDays < 0 ? 'Passed' : `${(limDays ?? 0).toLocaleString('en-GB')} days left`,
        },
        { icon: 'alert', label: 'Overdue', value: <span className={overdue.length ? 'text-[#c23b2c] dark:text-[#f3a094]' : ''}>{overdue.length}</span>, sub: overdue[0]?.deadlineType.name ?? 'None' },
        { icon: 'time', label: 'Due soon', value: soon.length, sub: 'Within 3 days · alerts on' },
        {
          icon: 'flag', label: 'Upcoming', value: later.length,
          sub: nextLater ? `Next: ${shortDay(ymdOf(nextLater.dueDate) ?? today)} · ${nextLater.deadlineType.name}` : 'None',
        },
      ]} />
      <div className="grid grid-cols-1 gap-[14px]">
        {deadlinesLoaded
          ? limitationCard
          // Limitation-card-sized placeholder (was a one-line "Loading…" card).
          : <div className={`${CARD} h-[132px] motion-safe:animate-pulse`} role="status" aria-label="Loading deadlines" />}
        {newCard}
        {deadlinesLoaded && rows.length === 0 && (
          <div className={`${CARD} px-6 py-6 text-center`}>
            <b className="text-[14.5px] font-semibold text-casevo-forest">No other deadlines on this case yet</b>
            <p className="text-[13px] text-casevo-text-soft mt-1">Add court dates, chasers and reminders here — the daily scan alerts on each one.</p>
          </div>
        )}
        {/* ONE flat keyed list (headers + cards as siblings), so a card that
            changes group — e.g. its date is edited from Upcoming into Due soon —
            is MOVED by React, not remounted: an open edit (and its Cancel
            snapshot) survives the regroup. */}
        {[
          ...(overdue.length > 0 ? [<GroupHead key="g-overdue" label="Overdue" count={overdue.length} />, ...overdue.map(renderRow)] : []),
          ...(soon.length > 0 ? [<GroupHead key="g-soon" label="Due soon" count={soon.length} />, ...soon.map(renderRow)] : []),
          ...(later.length > 0 ? [<GroupHead key="g-later" label="Upcoming" count={later.length} />, ...later.map(renderRow)] : []),
          ...(done.length > 0 ? [
            <GroupHead
              key="g-done"
              label="Completed"
              count={done.length}
              extra={
                <button type="button" onClick={() => setShowCompleted((v) => !v)} aria-expanded={showCompleted}
                  className="ml-auto normal-case tracking-normal font-semibold text-casevo-brand dark:text-[#8fd6b0] inline-flex items-center gap-[3px] focus:outline-none casevo-focus rounded-[3px]">
                  <KIcon name={showCompleted ? 'chevUp' : 'chev'} size={13} />{showCompleted ? 'Hide' : 'Show'}
                </button>
              }
            />,
            ...(showCompleted ? done.map(renderRow) : []),
          ] : []),
        ]}
      </div>
      {/* Alerts footer (mockup scanFoot) — Deadlines isn't in the setup chain. */}
      <div className={`${CARD} flex items-center gap-3 flex-wrap mt-4 py-[13px] pr-4 pl-[18px]`}>
        <span className="text-casevo-brand dark:text-[#8fd6b0] inline-flex shrink-0"><KIcon name="bell" size={18} /></span>
        <div className="text-[13.5px] text-casevo-forest min-w-0 flex-1">
          {/* Matches backend deadlineTaskScan: the fee earner + every case
              assignee; the firm's admins when nobody is assigned. */}
          {assignee ? `Deadline alerts go to the fee earner, ${assignee}, and everyone assigned to this case` : 'Deadline alerts go to everyone assigned to this case (the firm’s admins if nobody is)'}
          <span className="block text-[12px] text-casevo-text-soft">The 07:00 daily scan sends one alert once a deadline is within 3 days and one on the due day, in the bell and by email if that’s turned on. Completed deadlines are skipped.</span>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <button type="button" className={BTN_SEC} onClick={() => { void openCalendar(null) }}>
            <KIcon name="cal" size={15} />Open in calendar
          </button>
        </div>
      </div>
      {/* Delete-confirmation dialogue. */}
      <ConfirmDialogue
        isOpen={confirmDeleteDeadline !== null}
        title="Delete deadline?"
        message={
          confirmDeleteDeadline
            ? `The '${confirmDeleteDeadline.deadlineType.name}' deadline will be permanently deleted. This cannot be undone.${
                LIMITATION_RE.test(confirmDeleteDeadline.deadlineType.name) && !confirmDeleteDeadline.completed
                  ? ' This deadline will lose its required limitation warnings and overdue alerts.'
                  : ''}`
            : ''
        }
        confirmLabel={deletingId ? 'Deleting...' : 'Delete'}
        onConfirm={() => { void handleConfirmDelete() }}
        onCancel={() => setConfirmDeleteDeadline(null)}
      />
      {/* ★ Past-date guard for the one-click limitation add. */}
      <ConfirmDialogue
        isOpen={confirmPastLim !== null}
        title="This limitation date has already passed"
        message={confirmPastLim
          ? `The estimated limitation date (${limEst ? estimateBasisText(limEst.basis) : 'accident + 3y'}) is ${longDay(confirmPastLim.ymd)}, which is in the past. The claim may be statute-barred, or the accident date or date of birth may be wrong — check before relying on it. Add it as the limitation date anyway?`
          : ''}
        confirmLabel="Add anyway"
        tone="primary"
        onConfirm={() => { const c = confirmPastLim; setConfirmPastLim(null); if (c) void createDeadline(c.typeId, c.ymd, '') }}
        onCancel={() => setConfirmPastLim(null)}
      />
    </div>
  )
}

export default DeadlinesSection
